'use strict';

/**
 * Notes about browser support and related issues:
 *
 * Works on:
 * - macOS, Google Chrome
 * - Galaxy S6 Edge, Google Chrome, Samsung Internet, Oculus Home (GearVR),
 *   and Samsung Internet for GearVR
 * - Windows 10, Google Chrome and Microsoft Edge
 *
 * There are two issues that prevent support for Safari and Firefox. There
 * is a CORS issue on Safari, preventing video to load correctly from
 * other origins than the host. There is also a WebGL issue that occur on
 * both macOS Safari and Firefox.
 *
 * There are also problems for iOS because of the way it handle videos, with
 * automatic fullscreen etc.
 *
 * CORS issue:
 * - https://bugs.webkit.org/show_bug.cgi?id=135379
 * - https://github.com/mrdoob/three.js/issues/8110
 *
 * WebGL issue:
 * - https://github.com/mrdoob/three.js/issues/9716
 * - https://bugs.webkit.org/show_bug.cgi?id=171054
 * - https://bugzilla.mozilla.org/show_bug.cgi?id=1246410
 * - https://bugzilla.mozilla.org/show_bug.cgi?id=1322746
 *
 *
 * Does not work on IE11 because HTMLVideoElement is not a supported data
 * source in the WebGL texImage2D method. This could probably be resolved
 * with a custom fix for IE11, by rendering the video to a canvas and then
 * use the canvas as data source for texImage2D. More info at:
 * - https://msdn.microsoft.com/en-us/library/dn302435(v=vs.85).aspx
 * - https://github.com/mrdoob/three.js/issues/7560O
 *
 */

import Sizzle from 'sizzle';
import * as domUtils from '../util/dom';
import videoUtil from '../util/video-attributes';
import debug from '../util/debug';
import { VIDEO_FORMAT, EYE } from './immersive/types';
import hasImmersiveSupport from './immersive/has-immersive-support';
import ErrorType from '../constants/error-type';

const FULLSCREEN_CHANGE_EVENTS = 'fullscreenchange, mozfullscreenchange,' +
 'MSFullscreenChange, webkitfullscreenchange';
const NAMESPACE = 'immersive';

const Debug = debug('immersive');

function parseVideoFormatOrDefault(videoFormatString) {
    switch(videoFormatString) {
        case 'stereo':
        case 'stereo_tb':
            return VIDEO_FORMAT.Stereo_TB;
        case 'stereo_lr':
            return VIDEO_FORMAT.Stereo_LR;
        default:
            return VIDEO_FORMAT.Mono;
    }
}

function createVRButton(api, immersiveManager) {
    const button = document.createElement('div');
    button.className = 'svp_immersive-vr_button';

    domUtils.addEvents(button, 'click, touchend', function (e) {
        e.preventDefault();

        /**
         * We would like to start the video when the VRDisplay is presenting
         * the video. An issue is that play() must be called in response to a
         * user gesture. As such, api.play() is first called here, directly
         * in the event handler. The second api.play() call is when the
         * VRDisplay is ready and presenting, which is were we actually would
         * like to start playing.
         */

        api.play();
        immersiveManager.enterVR().then(() => {
            api.play();
        });
    });

    return button;
}

function getDashInstance(api, videoElement) {
    const uid = videoUtil.getUID(videoElement);
    if (!uid) {
        return;
    }
    const videoObject = api.video();
    if (!videoObject || typeof (videoObject.getDashInstances) !== 'function') {
        return;
    }
    const dashInstances = videoObject.getDashInstances();
    if (!dashInstances) {
        return;
    }

    return dashInstances[uid];
}

function updateDashInstance(dashInstance) {
    if (!dashInstance) {
        return;
    }

    // Turn off limit bitrate by portal. A 360 video does not
    // render the entire video to the video element viewport.
    // Instead, it will render a part of the video source. As such,
    // the video element size and video resolution does not match.
    dashInstance.mediaPlayer.setLimitBitrateByPortal(false);
}

const SizzleSingle = (selector, context) => {
    const result = Sizzle(selector, context);

    return result.length > 0 ? result[0] : null;
};

const queryVideoElement = (api) => SizzleSingle('video', api.element());

class ImmersivePlugin {

    constructor(namespace, api) {
        this.namespace = namespace;
        this.api = api;
        this.videoElement = queryVideoElement(api);
        this.dashInstance = null;
        this.immersiveManager = null;

        this.api.on(this.namespace + '.metadata', (metadata) => {
            const isImmersiveVideo = videoUtil.isImmersiveVideo(this.videoElement);

            if (isImmersiveVideo) {
                this.setupImmersive();
            } else {
                this.teardownImmersive();
            }
        });
    }

    handleImmersiveNotSupported() {
        // The unknownVideoTypeError can occur after the immersiveNotSupported
        // is fired. For example, the stream might be dash only and the user
        // is using Safari. We prevent this error so that it does not replace
        // the immersive error message.
        this.api.once(this.namespace + '.' + ErrorType.UNKNOW_VIDEO_TYPE_ERROR, (e) => {
            e.preventDefault();
        });

        this.api.fire('error', {
            name: ErrorType.IMMERSIVE_NOT_SUPPORTED_ERROR
        });
    }

    setupImmersive() {
        if (!hasImmersiveSupport()) {
            this.handleImmersiveNotSupported();

            return;
        }

        domUtils.addClass(this.api.element(), 'svp_immersive');
        this.videoElement.crossOrigin = 'anonymous';

        this.addEventListeners();

        // Dynamically import ImmersiveManager, which will also load its
        // heavy dependencies (three and webvr-polyfill).
        //
        // TODO: Use import() instead of require.ensure(), because it is
        // specific to webpack and is superseeded by ES syntax import().
        require.ensure([], (require) => {
            const ImmersiveManager = require('./immersive/immersive-manager');

            const videoFormatString = videoUtil.getImmersiveVideoFormat(this.videoElement);
            const videoFormat = parseVideoFormatOrDefault(videoFormatString);

            Debug.log(`Video Format: ${videoFormatString} (${videoFormat})`);

            this.immersiveManager = new ImmersiveManager(this.videoElement, videoFormat);

            // Return immediately if any error occured.
            if (this.immersiveManager.isInErrorState()) {
                this.api.fire('error');

                return;
            }

            this.immersiveManager.initialize().then(() => {
                // Insert VR button if VR mode is allowed.
                if (this.immersiveManager.canEnterVR()) {
                    this.api.insertButton(createVRButton(this.api, this.immersiveManager));
                }

                this.immersiveManager.run();

                // Append to DOM
                this.api.element().appendChild(this.immersiveManager.container);

                Debug.log('Immersive plugin is enabled and running.');
            });
        }, (err) => {
            Debug.error('Failed to load immersive plugin because:', err);
            this.api.fire('error', {
                name: ErrorType.IMMERSIVE_FAILED_TO_LOAD
            });
        }, 'immersive');
    }

    teardownImmersive() {
        if (this.immersiveManager && this.immersiveManager.container) {
            this.api.element().removeChild(this.immersiveManager.container);
        }

        domUtils.removeClass(this.api.element(), 'svp_immersive');

        if (this.immersiveManager) {
            this.immersiveManager = null;
        }
    }

    addEventListeners() {
        this.api.on(this.namespace + '.ready', () => {
            this.dashInstance = getDashInstance(this.api, this.videoElement);
            updateDashInstance(this.dashInstance);
        });

        this.api.on(this.namespace + '.dashmanifestloaded', () => {
            updateDashInstance(this.dashInstance);
        });

        // Prevent automatic pause when the user enter VR mode, which occur
        // on Samsung Browser (the default Internet app).
        this.api.on(this.namespace + '.pause', (event) => {
            if (this.immersiveManager && this.immersiveManager.inVRMode()) {
                Debug.log('Applying do not pause fix');
                window.setTimeout(() => this.api.play(), 50);
            }
        });

        const handleResize = () => {
            if (this.immersiveManager && !this.immersiveManager.isInErrorState()) {
                this.immersiveManager.updateSize();
            }
        };
        this.api.on(this.namespace + '.resize', handleResize);
        domUtils.addEvents(window, 'resize', handleResize, this.namespace);
        domUtils.addEvents(document, FULLSCREEN_CHANGE_EVENTS, handleResize, this.namespace);

        // Add mouse events on window to add dragging class on the player
        // container element. This allows the CSS to change cursor when
        // dragging occurs.
        domUtils.addEvents(window, 'mousedown', () => {
            domUtils.addClass(this.api.element(), 'svp_immersive--dragging');
        }, this.namespace);
        domUtils.addEvents(window, 'mouseup', () => {
            domUtils.removeClass(this.api.element(), 'svp_immersive--dragging');
        }, this.namespace);
    }

    removeEventListeners() {
        this.api.off(this.namespace + '.metadata');
        this.api.off(this.namespace + '.ready');
        this.api.off(this.namespace + '.dashmanifestloaded');
        this.api.off(this.namespace + '.resize');
        this.api.off(this.namespace + '.' + ErrorType.UNKNOW_VIDEO_TYPE_ERROR);

        domUtils.removeEvents(window, 'resize', this.namespace);
        domUtils.removeEvents(document, FULLSCREEN_CHANGE_EVENTS, this.namespace);
        domUtils.removeEvents(window, 'mousedown', this.namespace);
        domUtils.removeEvents(window, 'mouseup', this.namespace);
    }

    destroy() {
        this.removeEventListeners();
        this.teardownImmersive();
    }
}

module.exports = {
    namespace: NAMESPACE,
    _pluginInstance: null,

    create: function (api) {
        this._pluginInstance = new ImmersivePlugin(this.namespace, api);
    },

    destroy: function (api) {
        if (this._pluginInstance) {
            this._pluginInstance.destroy();
        }
    }
};




// WEBPACK FOOTER //
// ./src/js/plugins/immersive.js